// src/pages/CRM/ActivityComm/WorkflowList.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, TextField, Switch, CircularProgress,
  Alert, Stack, Tooltip, Pagination, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { workflowService } from './activityCommService';
import {
  WorkflowType,
  WorkflowTypeLabels,
  WorkflowTypeColors,
  WorkflowTriggerType,
  WorkflowTriggerTypeLabels,
  WorkflowExecutionStatus,
  WorkflowExecutionStatusLabels,
  WorkflowExecutionStatusColors,
  formatDateTime
} from './activityCommEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const WorkflowList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 20;

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);

  // Trigger dialog
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [workflowToTrigger, setWorkflowToTrigger] = useState(null);
  const [contactIdsInput, setContactIdsInput] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: searchTerm || undefined,
        workflow_type: typeFilter || undefined
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await workflowService.getAll(params);
      if (response.success) {
        setWorkflows(response.data?.workflows || []);
        setTotalPages(response.data?.pages || 1);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load workflows', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, searchTerm, typeFilter, enqueueSnackbar]);

  useEffect(() => {
    document.title = 'Workflows | Activity & Communications';
    loadWorkflows();
  }, [loadWorkflows]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleToggleActive = async (workflow) => {
    try {
      const response = await workflowService.toggleActive(workflow.id);
      if (response.success) {
        enqueueSnackbar(
          `Workflow ${response.data?.is_active ? 'activated' : 'deactivated'} successfully`,
          { variant: 'success' }
        );
        loadWorkflows();
      }
    } catch (err) {
      enqueueSnackbar('Failed to toggle workflow status', { variant: 'error' });
      console.error(err);
    }
  };

  // Delete handlers
  const openDeleteDialog = (workflow) => {
    setWorkflowToDelete(workflow);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setWorkflowToDelete(null);
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;
    try {
      const response = await workflowService.delete(workflowToDelete.id);
      if (response.success) {
        enqueueSnackbar('Workflow deleted successfully', { variant: 'success' });
        loadWorkflows();
      }
    } catch (err) {
      enqueueSnackbar('Failed to delete workflow', { variant: 'error' });
      console.error(err);
    } finally {
      closeDeleteDialog();
    }
  };

  // Trigger handlers
  const openTriggerDialog = (workflow) => {
    setWorkflowToTrigger(workflow);
    setContactIdsInput('');
    setTriggerDialogOpen(true);
  };

  const closeTriggerDialog = () => {
    setTriggerDialogOpen(false);
    setWorkflowToTrigger(null);
    setContactIdsInput('');
  };

  const handleTrigger = async () => {
    if (!workflowToTrigger) return;
    const contactIds = contactIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0)
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));

    if (contactIds.length === 0) {
      enqueueSnackbar('Please enter at least one contact ID', { variant: 'warning' });
      return;
    }

    setTriggerLoading(true);
    try {
      const response = await workflowService.trigger(workflowToTrigger.id, { contact_ids: contactIds });
      if (response.success) {
        enqueueSnackbar('Workflow triggered successfully', { variant: 'success' });
        closeTriggerDialog();
      }
    } catch (err) {
      enqueueSnackbar('Failed to trigger workflow', { variant: 'error' });
      console.error(err);
    } finally {
      setTriggerLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 }, fontFamily }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontFamily, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Workflows
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
            Automate contact communication, reminders, and follow-ups
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows/new`)}
          sx={{ fontFamily }}
        >
          New Workflow
        </Button>
      </Box>

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Search workflows by name..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ fontFamily }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Workflow Type</InputLabel>
            <Select
              value={typeFilter}
              label="Workflow Type"
              onChange={handleTypeFilterChange}
            >
              <MenuItem value="">All Types</MenuItem>
              {Object.entries(WorkflowTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Workflows Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Trigger</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Steps</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Active</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Created At</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : workflows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontFamily }}>
                      No workflows found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workflows.map((workflow) => (
                  <TableRow key={workflow.id} hover>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontWeight: 500, fontFamily }}>
                        {workflow.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={WorkflowTypeLabels[workflow.workflow_type] || workflow.workflow_type}
                        color={WorkflowTypeColors[workflow.workflow_type] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily }}>
                        {WorkflowTriggerTypeLabels[workflow.trigger_type] || workflow.trigger_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily }}>
                        {workflow.steps_count ?? workflow.steps?.length ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!workflow.is_active}
                        onChange={() => handleToggleActive(workflow)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(workflow.created_at)}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
                          {formatDateTime(workflow.created_at)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows/${workflow.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows/${workflow.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Trigger">
                          <IconButton
                            size="small"
                            onClick={() => openTriggerDialog(workflow)}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeleteDialog(workflow)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle sx={{ fontFamily }}>Delete Workflow</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily }}>
            Are you sure you want to delete the workflow "{workflowToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} sx={{ fontFamily }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ fontFamily }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trigger Dialog */}
      <Dialog open={triggerDialogOpen} onClose={closeTriggerDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily }}>
          Trigger Workflow: {workflowToTrigger?.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily, mb: 2 }}>
            Enter comma-separated contact IDs to trigger this workflow for.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Contact IDs"
            placeholder="e.g. 1, 2, 3"
            value={contactIdsInput}
            onChange={(e) => setContactIdsInput(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTriggerDialog} sx={{ fontFamily }}>Cancel</Button>
          <Button
            onClick={handleTrigger}
            variant="contained"
            disabled={triggerLoading}
            startIcon={triggerLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            sx={{ fontFamily }}
          >
            Trigger
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowList;
